import { formatDateAsDayMonthYear } from '../../../utils/date/dateFormat'
import {
  AME_CAMPAIGN_STATUS,
  AME_CAMPAIGN_STATUS_LABELS,
  AME_CAMPAIGN_SCOPE_LABELS,
  AME_PROMOTION_TYPE_LABEL,
} from './ameCampaignConstants'
import { PROMO_TYPE_VARIANT } from '../promoScheduleConstants'

const toMillis = (value) => {
  if (value == null) return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value.toMillis === 'function') return value.toMillis()
  if (typeof value.seconds === 'number') {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6)
  }
  if (value instanceof Date) return value.getTime()
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

const toSecondsStamp = (value) => {
  const millis = toMillis(value)
  if (millis == null) return null
  return { seconds: Math.floor(millis / 1000) }
}

const formatBenefitLabel = (benefit) => {
  if (!benefit || typeof benefit !== 'object') return '—'
  if (benefit.kind === 'fixedAmount') {
    const amount = Number(benefit.amount)
    if (!Number.isFinite(amount) || amount <= 0) return '—'
    const formatted = Number.isInteger(amount) ? String(amount) : String(amount).replace('.', ',')
    return `${formatted} ₴`
  }
  const percent = benefit.percent
  return Number.isInteger(percent) ? `${percent}%` : '—'
}

const formatKyivRange = (startsAt, endsAt) => {
  const startStamp = toSecondsStamp(startsAt)
  if (!startStamp) return '—'
  const startLabel = formatDateAsDayMonthYear(startStamp)
  if (endsAt == null) return `${startLabel} — без завершення`
  const endStamp = toSecondsStamp(endsAt)
  if (!endStamp) return startLabel
  return `${startLabel} — ${formatDateAsDayMonthYear(endStamp)}`
}

const formatUaCount = (count, one, few, many) => {
  const n = Math.abs(Number(count) || 0)
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`
  return `${n} ${many}`
}

/**
 * Exclude summary for the campaigns table.
 * Segments: bold category count + full plain list of names when known.
 */
export const formatExcludeSummary = (exclude = {}, brandsById = {}) => {
  const brandIds = Array.isArray(exclude?.brandIds) ? exclude.brandIds.map(String).filter(Boolean) : []
  const categoryIds = Array.isArray(exclude?.categoryNodeIds) ? exclude.categoryNodeIds.map(String).filter(Boolean) : []
  const productRefs = Array.isArray(exclude?.productRefs) ? exclude.productRefs.filter(Boolean) : []

  if (!brandIds.length && !categoryIds.length && !productRefs.length) {
    return { segments: [], hasExclusions: false }
  }

  const segments = []

  if (brandIds.length) {
    const names = brandIds
      .map((id) => {
        const name = brandsById[id]?.name
        return typeof name === 'string' && name.trim() ? name.trim() : null
      })
      .filter(Boolean)
    segments.push({
      countLabel: formatUaCount(brandIds.length, 'бренд', 'бренди', 'брендів'),
      list: names.join(', '),
    })
  }

  if (categoryIds.length) {
    segments.push({
      countLabel: formatUaCount(categoryIds.length, 'категорія', 'категорії', 'категорій'),
      list: '',
    })
  }

  if (productRefs.length) {
    segments.push({
      countLabel: formatUaCount(productRefs.length, 'товар', 'товари', 'товарів'),
      list: '',
    })
  }

  return { segments, hasExclusions: true }
}

export const mapAmeCampaignListItem = (id, data = {}, { brandsById = {} } = {}) => {
  const status = data.status || AME_CAMPAIGN_STATUS.draft
  const percent = data.benefit?.percent
  const benefitLabel = formatBenefitLabel(data.benefit)
  const isActive = data.isActive === true || (data.isActive == null && status === AME_CAMPAIGN_STATUS.published)
  const updatedMs = toMillis(data.updatedAt) || 0
  const excludeSummary = formatExcludeSummary(data.exclude, brandsById)

  return {
    id,
    title: data.title || '—',
    issue: data.issue || null,
    status,
    statusLabel: AME_CAMPAIGN_STATUS_LABELS[status] || status,
    isActive,
    promotionTypeLabel: AME_PROMOTION_TYPE_LABEL,
    promoTypeVariant: PROMO_TYPE_VARIANT.ame,
    percent: Number.isInteger(percent) ? percent : null,
    percentLabel: benefitLabel,
    benefitLabel,
    scope: data.scope,
    scopeLabel: AME_CAMPAIGN_SCOPE_LABELS[data.scope] || '—',
    applyToSpecPrice: data.applyToSpecPrice === true,
    dateRangeLabel: formatKyivRange(data.startsAt, data.endsAt),
    issuedCount: Number.isInteger(data.issuedCount) ? data.issuedCount : null,
    redeemedCount: Number.isInteger(data.redeemedCount) ? data.redeemedCount : null,
    excludeSegments: excludeSummary.segments,
    hasExclusions: excludeSummary.hasExclusions,
    updated: toSecondsStamp(data.updatedAt),
    updatedAtSort: updatedMs,
    raw: { id, ...data },
  }
}

export default mapAmeCampaignListItem
